import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { 
  useGetUsersQuery, 
  useGetRolesQuery, 
  useAddUserRoleMutation 
} from '../../api/userAdministrationApi';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Table, 
  Form, 
  Button, 
  Badge, 
  Spinner, 
  Alert, 
  Modal,
  InputGroup
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaSearch, FaUsersCog, FaFilter, FaTimes, FaCheck, FaUserPlus } from 'react-icons/fa';
import Navbar from '../Navbar/Navbar';
import Footer from '../footer/Footer';

const RoleManagement = () => {
  // Auth state
  const authState = useSelector(state => state.authStore);
  const [isAdmin, setIsAdmin] = useState(false);

  // API queries
  const { data: usersData, isLoading: usersLoading, error: usersError, refetch: refetchUsers } = useGetUsersQuery({
    page: 1,
    pageSize: 500 // Daha fazla kullanıcı için arttırıldı
  });
  const { data: roles, isLoading: rolesLoading } = useGetRolesQuery();
  const [addUserRole, { isLoading: isAddingRole }] = useAddUserRoleMutation();

  // Debug information
  console.log("Component rendering with:", {
    authState,
    isAdmin,
    usersLoading,
    rolesLoading,
    usersData: usersData ? 'has data' : 'no data',
    roles: roles ? 'has data' : 'no data'
  });

  console.log("trigger is admin", authState)

  // Debug API responses
  useEffect(() => {
    console.log("Auth state updated:", authState);
    // Only check for Admin role if user and role exist
    if (authState && authState.user && authState.user.role) {
      // Check if role is a string array or object array
      let adminStatus = false;
      if (Array.isArray(authState.user.role)) {
        // If roles are strings like ["Admin", "User"]
        adminStatus = authState.user.role.some(r => 
          typeof r === 'object' ? r.name === 'Admin' : r === 'Admin'
        );
      } else if (typeof authState.user.role === 'string') {
        // If role is a single string "Admin,User"
        adminStatus = authState.user.role.split(',').includes('Admin');
      }
      console.log("Setting isAdmin to:", adminStatus);
      setIsAdmin(!!adminStatus); // Convert to boolean with !!
    }
  }, [authState]);

  useEffect(() => {
    if (usersData) {
      console.log('Users API Response:', usersData);
      
      // Log a sample user to see the structure
      if (usersData.result && usersData.result.users && usersData.result.users.length > 0) {
        console.log('Sample user structure:', usersData.result.users[0]);
        console.log('Sample user roles:', usersData.result.users[0].roles);
      }
    }
    if (roles) {
      console.log('Roles API Response:', roles);
    }
  }, [usersData, roles]);

  // State
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [roleToAssign, setRoleToAssign] = useState('');
  const [selectedRoleName, setSelectedRoleName] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  
  // Filter users when data changes or filter criteria change
  useEffect(() => {
    if (usersData && usersData.result && usersData.result.users) {
      console.log('API Response Structure:', usersData);
      
      // Get users array from the response
      const usersArray = usersData.result.users || [];
      
      // Make sure we're working with an array
      if (Array.isArray(usersArray)) {
        let result = [...usersArray];
        
        // Apply search filter
        if (searchTerm) {
          result = result.filter(user => 
            user.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email?.toLowerCase().includes(searchTerm.toLowerCase())
          );
        }
        
        // Apply role filter
        if (selectedRole) {
          result = result.filter(user => 
            user.roles && Array.isArray(user.roles) && user.roles.some(role => {
              // Handle role whether it's an object or string
              const roleName = typeof role === 'object' ? role.name : role;
              return roleName.toLowerCase() === selectedRole.toLowerCase();
            })
          );
        }
        
        setFilteredUsers(result);
        console.log('Filtered users:', result);
      } else {
        console.error('Users data is not an array:', usersArray);
        setFilteredUsers([]);
      }
    } else {
      setFilteredUsers([]);
    }
  }, [usersData, searchTerm, selectedRole]);

  // If not admin, show access denied
  if (!isAdmin && authState && authState.user) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <Alert.Heading>Erişim Reddedildi</Alert.Heading>
          <p>
            Bu sayfayı görüntülemek için admin yetkisine sahip olmanız gerekmektedir.
          </p>
        </Alert>
      </Container>
    );
  }

  // Handle role assignment
  const handleAssignRole = async () => {
    if (!selectedUser || !roleToAssign) return;
    
    try {
      // roleToAssign artık roleId içeriyor
      await addUserRole({
        userId: selectedUser.id,
        roleId: roleToAssign
      }).unwrap();
      
      // Başarı mesajında göstermek için rol adını bulalım
      const selectedRoleName = roles && (Array.isArray(roles) ? roles : (roles.result || []))
        .find(role => (typeof role === 'object' ? role.id : role) === roleToAssign);
      const roleName = selectedRoleName ? (typeof selectedRoleName === 'object' ? selectedRoleName.name : selectedRoleName) : roleToAssign;
      
      toast.success(`${roleName} rolü ${selectedUser.userName} kullanıcısına başarıyla atandı`);
      refetchUsers();
      setShowModal(false);
    } catch (error) {
      console.error('Role assignment error:', error);
      toast.error(error.data?.message || error.data?.errorMessages?.join(', ') || 'Rol atama işlemi başarısız oldu');
    }
  };

  // Show role assignment modal
  const openAssignModal = (user) => {
    setSelectedUser(user);
    setRoleToAssign('');
    setShowModal(true);
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedRole('');
  };

  // Show role removal confirmation
  const confirmRemoveRole = (user, role) => {
    setSelectedUser(user);
    // roleId'yi kaydetmek için 
    setRoleToAssign(typeof role === 'object' ? role.id : role);
    // Silme onay modalı için rol adını görüntüleme amacıyla
    setSelectedRoleName(typeof role === 'object' ? role.name : role);
    setShowConfirmModal(true);
  };

  // Handle role removal
  const handleRemoveRole = async () => {
    try {
      // Using the same API but with remove: true flag
      await addUserRole({
        userId: selectedUser.id,
        roleId: roleToAssign,
        remove: true
      }).unwrap();
      
      toast.success(`${selectedRoleName} rolü ${selectedUser.userName} kullanıcısından başarıyla kaldırıldı`);
      refetchUsers();
      setShowConfirmModal(false);
    } catch (error) {
      console.error('Role removal error:', error);
      toast.error(error.data?.message || error.data?.errorMessages?.join(', ') || 'Rol kaldırma işlemi başarısız oldu');
    }
  };

  // Loading state
  if (usersLoading || rolesLoading) {
    return (
      <Container className="d-flex justify-content-center mt-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Yükleniyor...</span>
        </Spinner>
      </Container>
    );
  }

  // Error state
  if (usersError) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <Alert.Heading>Hata</Alert.Heading>
          <p>
            {usersError.data?.message || 'Kullanıcı listesi yüklenirken bir hata oluştu'}
          </p>
        </Alert>
      </Container>
    );
  }

  return (
    <>
    <Navbar></Navbar>
    <Container fluid className="mt-4 mb-5">
      <Row>
        <Col>
          <Card className="shadow-sm">
            <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
              <div>
                <FaUsersCog className="me-2" />
                <span className="fw-bold">Rol Yönetimi</span>
              </div>
              <Badge bg="light" text="dark" pill>
                {filteredUsers.length} Kullanıcı
              </Badge>
            </Card.Header>
            <Card.Body>
              {/* Filter Section */}
              <Row className="mb-4">
                <Col md={5}>
                  <InputGroup>
                    <InputGroup.Text>
                      <FaSearch />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="İsim veya e-posta ile ara..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                      <Button 
                        variant="outline-secondary"
                        onClick={() => setSearchTerm('')}
                      >
                        <FaTimes />
                      </Button>
                    )}
                  </InputGroup>
                </Col>
                <Col md={4}>
                  <InputGroup>
                    <InputGroup.Text>
                      <FaFilter />
                    </InputGroup.Text>
                    <Form.Select
                      value={selectedRole}
                      onChange={(e) => setSelectedRole(e.target.value)}
                    >
                      <option value="">Tüm Roller</option>
                      {roles && (Array.isArray(roles) ? roles : (roles.result || [])).map(role => {
                        // Check if role is an object or a string
                        const roleId = typeof role === 'object' ? role.id : role;
                        const roleName = typeof role === 'object' ? role.name : role;
                        return (
                          <option key={roleId} value={roleName}>{roleName}</option>
                        );
                      })}
                    </Form.Select>
                  </InputGroup>
                </Col>
                <Col md={3} className="d-flex justify-content-end">
                  <Button 
                    variant="outline-secondary" 
                    onClick={clearFilters}
                    disabled={!searchTerm && !selectedRole}
                    className="me-2"
                  >
                    Filtreleri Temizle
                  </Button>
                  <Button 
                    variant="outline-primary" 
                    onClick={() => refetchUsers()}
                  >
                    Yenile
                  </Button>
                </Col>
              </Row>

              {/* Users Table */}
              <div className="table-responsive">
                <Table striped hover className="align-middle">
                  <thead>
                    <tr>
                      <th>Kullanıcı Adı</th>
                      <th>E-posta</th>
                      <th>Roller</th>
                      <th className="text-center">İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.length > 0 ? (
                      filteredUsers.map(user => (
                        <tr key={user.id}>
                          <td>{user.userName}</td>
                          <td>{user.email}</td>
                          <td>
                            {user.roles && Array.isArray(user.roles) && user.roles.length > 0 ? (
                              <div className="d-flex flex-wrap gap-1">
                                {user.roles.map(role => {
                                  // Check if role is an object or a string
                                  const roleId = typeof role === 'object' ? role.id : role;
                                  const roleName = typeof role === 'object' ? role.name : role;
                                  return (
                                    <Badge 
                                      key={roleId} 
                                      bg={roleName === 'Admin' ? 'danger' : roleName === 'Instructor' ? 'success' : 'info'}
                                      className="d-flex align-items-center"
                                    >
                                      {roleName}
                                      <Button
                                        size="sm"
                                        variant="link"
                                        className="p-0 ms-1 text-white"
                                        onClick={() => confirmRemoveRole(user, role)}
                                      >
                                        <FaTimes size={10} />
                                      </Button>
                                    </Badge>
                                  );
                                })}
                              </div>
                            ) : (
                              <span className="text-muted">Rol atanmamış</span>
                            )}
                          </td>
                          <td className="text-center">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => openAssignModal(user)}
                            >
                              <FaUserPlus className="me-1" />
                              Rol Ata
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center py-3">
                          <Alert variant="light" className="mb-0">
                            Filtrelere uygun kullanıcı bulunamadı
                          </Alert>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Role Assignment Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Rol Ata</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <>
              <p>
                <strong>{selectedUser.userName}</strong> kullanıcısına rol atamak üzeresiniz.
              </p>
              
              <Form.Group className="mb-3">
                <Form.Label>Atanacak Rol</Form.Label>
                <Form.Select
                  value={roleToAssign}
                  onChange={(e) => setRoleToAssign(e.target.value)}
                >
                  <option value="">Rol seçin</option>
                  {roles && (Array.isArray(roles) ? roles : (roles.result || []))
                    .filter(role => {
                      if (!selectedUser.roles || !Array.isArray(selectedUser.roles)) {
                        return true;
                      }
                      // Get the role name to check against
                      const roleToCheck = typeof role === 'object' ? role.name : role;
                      // Check if the user already has this role
                      return !selectedUser.roles.some(userRole => 
                        (typeof userRole === 'object' ? userRole.name : userRole) === roleToCheck
                      );
                    })
                    .map(role => {
                      // Extract role id and name if it's an object
                      const roleId = typeof role === 'object' ? role.id : role;
                      const roleName = typeof role === 'object' ? role.name : role;
                      return (
                        <option key={roleId} value={roleId}>{roleName}</option>
                      );
                    })
                  }
                </Form.Select>
              </Form.Group>
              
              <div className="mt-3">
                <strong>Mevcut Roller:</strong>
                <div className="d-flex flex-wrap gap-1 mt-2">
                  {selectedUser.roles && Array.isArray(selectedUser.roles) && selectedUser.roles.length > 0 ? (
                    selectedUser.roles.map(role => {
                      // Check if role is an object or a string
                      const roleId = typeof role === 'object' ? role.id : role;
                      const roleName = typeof role === 'object' ? role.name : role;
                      return (
                        <Badge 
                          key={roleId} 
                          bg={roleName === 'Admin' ? 'danger' : roleName === 'Instructor' ? 'success' : 'info'}
                        >
                          {roleName}
                        </Badge>
                      );
                    })
                  ) : (
                    <span className="text-muted">Rol atanmamış</span>
                  )}
                </div>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            İptal
          </Button>
          <Button 
            variant="primary" 
            onClick={handleAssignRole}
            disabled={!roleToAssign || isAddingRole}
          >
            {isAddingRole ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                <span className="ms-2">İşleniyor...</span>
              </>
            ) : (
              <>
                <FaCheck className="me-1" />
                Rol Ata
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Role Removal Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Rolü Kaldır</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && roleToAssign && (
            <p>
              <strong>{selectedUser.userName}</strong> kullanıcısından <Badge 
                bg={selectedRoleName === 'Admin' ? 'danger' : selectedRoleName === 'Instructor' ? 'success' : 'info'}
              >
                {selectedRoleName}
              </Badge> rolünü kaldırmak istediğinize emin misiniz?
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            İptal
          </Button>
          <Button 
            variant="danger" 
            onClick={handleRemoveRole}
            disabled={isAddingRole}
          >
            {isAddingRole ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                <span className="ms-2">İşleniyor...</span>
              </>
            ) : (
              <>
                <FaTimes className="me-1" />
                Rolü Kaldır
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
   <Footer></Footer>
  </>
  );
};

export default RoleManagement;
